/**
 * UPT-2079 L3 (§5.4) — disposition automatique MAISON (aucune dépendance : ni dagre, ni elk), fonction pure testée.
 * `autoLayout(spec, layout)` place les entités SANS position par rangs : rang 0 = entités qui ne sont cible d'aucune
 * relation interne et `kind ≠ 'child'` ; rang suivant = cibles / enfants (`parent.entity`) des rangs précédents
 * (parcours en largeur, cycles coupés à l'ordre des codes) ; colonne par rang (`x = 320 · rang`), ligne par ordre
 * d'apparition (`y = 160 · i`), en évitant les positions déjà prises. Les positions existantes sont conservées telles
 * quelles. `externalPositions` place les nœuds fantômes (relations vers un autre module) à droite du dernier rang.
 */
import type { DesignModuleSpecJson, DesignerNodePosition, ModuleSpecDraftLayout } from '../data/contracts';
import { asRecord, asRecords, asString } from './specTypes';

export const COLUMN_GAP = 320;
export const ROW_GAP = 160;

/** Rang de chaque entité (0 = racines), parcours en largeur, cycles coupés à l'ordre des codes. */
export const entityRanks = (spec: DesignModuleSpecJson): Map<string, number> => {
  const entities = asRecord(spec.entities) ?? {};
  const codes = Object.keys(entities);
  const successors = new Map<string, Set<string>>();
  const incoming = new Map<string, number>();
  codes.forEach((code) => {
    successors.set(code, new Set());
    incoming.set(code, 0);
  });
  const link = (from: string, to: string): void => {
    if (from === to || !successors.has(from) || !successors.has(to)) return;
    const set = successors.get(from) as Set<string>;
    if (!set.has(to)) {
      set.add(to);
      incoming.set(to, (incoming.get(to) ?? 0) + 1);
    }
  };
  asRecords(spec.relations).forEach((relation) => {
    const source = asString(asRecord(relation.source)?.entity);
    const target = asRecord(relation.target);
    if (target && typeof target.module !== 'string') link(source, asString(target.entity));
  });
  codes.forEach((code) => {
    const entity = asRecord(entities[code]);
    const parent = asString(asRecord(entity?.parent)?.entity);
    if (parent) link(parent, code);
  });

  const ranks = new Map<string, number>();
  const isChild = (code: string): boolean => asRecord(entities[code])?.kind === 'child';
  let frontier = codes.filter((code) => (incoming.get(code) ?? 0) === 0 && !isChild(code));
  if (frontier.length === 0 && codes.length > 0) frontier = [codes[0]];
  let rank = 0;
  while (frontier.length > 0) {
    const next: string[] = [];
    frontier.forEach((code) => {
      if (ranks.has(code)) return;
      ranks.set(code, rank);
    });
    frontier.forEach((code) => {
      [...(successors.get(code) ?? [])].sort().forEach((target) => {
        if (!ranks.has(target) && !next.includes(target)) next.push(target);
      });
    });
    frontier = next;
    rank += 1;
    // Cycle non atteint : reprise à la première entité restante (ordre des codes).
    if (frontier.length === 0) {
      const remaining = codes.find((code) => !ranks.has(code));
      if (remaining) frontier = [remaining];
    }
  }
  return ranks;
};

const taken = (layout: ModuleSpecDraftLayout): Set<string> =>
  new Set(Object.values(layout.entities).map((position) => `${position.x}:${position.y}`));

export const autoLayout = (spec: DesignModuleSpecJson, layout: ModuleSpecDraftLayout): ModuleSpecDraftLayout => {
  const entities = asRecord(spec.entities) ?? {};
  const codes = Object.keys(entities);
  const positions: Record<string, DesignerNodePosition> = {};
  codes.forEach((code) => {
    const existing = layout.entities[code];
    if (existing && Number.isFinite(existing.x) && Number.isFinite(existing.y)) positions[code] = { x: existing.x, y: existing.y };
  });
  const missing = codes.filter((code) => !positions[code]);
  if (missing.length === 0) return { ...layout, entities: positions };
  const ranks = entityRanks(spec);
  const occupied = taken({ entities: positions });
  const rowByRank = new Map<number, number>();
  missing.forEach((code) => {
    const rank = ranks.get(code) ?? 0;
    let row = rowByRank.get(rank) ?? 0;
    let candidate = { x: COLUMN_GAP * rank, y: ROW_GAP * row };
    while (occupied.has(`${candidate.x}:${candidate.y}`)) {
      row += 1;
      candidate = { x: COLUMN_GAP * rank, y: ROW_GAP * row };
    }
    rowByRank.set(rank, row + 1);
    occupied.add(`${candidate.x}:${candidate.y}`);
    positions[code] = candidate;
  });
  return { ...layout, entities: positions };
};

/** Positions des nœuds fantômes externes (`{module}:{entity}`) : colonne à droite du dernier rang occupé. */
export const externalPositions = (layout: ModuleSpecDraftLayout, externalKeys: readonly string[]): Record<string, DesignerNodePosition> => {
  const maxX = Object.values(layout.entities).reduce((max, position) => Math.max(max, position.x), -COLUMN_GAP);
  const x = maxX + COLUMN_GAP;
  const out: Record<string, DesignerNodePosition> = {};
  [...externalKeys].sort().forEach((key, index) => {
    out[key] = { x, y: ROW_GAP * index };
  });
  return out;
};
